// Casting: one send's answer to "who is in each role".
//
// CONSUMER: shared (Workers send route + web prep screen + node-pool tests).
//
// The catalog (signing-roles.ts) says what the seats are and the rule
// that fills each. This runs those rules against a record and gives
// three honest outcomes per seat, never a silent guess (the accuracy
// rule: a missing signature line is the one mistake that looks
// finished):
//
//   - sure:   the rule found exactly one person. Shown with the reason.
//   - ask:    the rule found more than one. The row shows the candidates
//             and the send is blocked until someone picks.
//   - nobody: the rule found no one. An optional seat is simply left off
//             (a single buyer signs a two-buyer form every week); a
//             required seat blocks the send until someone is picked or
//             the seat is deliberately left off.
//   - manual: the rule is "you pick each time"; the row opens empty and
//             does not block on its own.
//
// Pure and dependency-free: the caller loads the record's people, its
// parties and its owner; nothing here touches a database.

import {
  fillRulePlainEnglish,
  firstSeatOf,
  secondSeatOf,
  type RoleKind,
  type SigningRole,
} from "./signing-roles";

/** Someone who can fill a seat. `key` is the signer key a placed field
 *  addresses; for a person it is their contact id, for a party the
 *  party's own key, for the owner the literal "owner". */
export interface CastPerson {
  key: string;
  kind: "person" | "party" | "owner" | "name";
  contactId: string | null;
  displayName: string;
  email: string | null;
  /** For a party: what the Parties list calls them. */
  partyRole?: string;
  /** For the record's owner ONLY: which user they are.
   *
   *  Carried because a stamp puts a real person's signature on a legal
   *  document, and "is this seat the person sending?" cannot be answered
   *  from a display name — two advisors share a first name and one of
   *  them is not entitled to the other's hand. Null on every other cast
   *  kind, which is why seatsTheSenderStamps refuses them all. */
  ownerUserId?: string | null;
}

export interface CastingContext {
  /** The record's own people, primary first, in the record's order. */
  people: readonly {
    contactId: string;
    displayName: string;
    email: string | null;
    isPrimary?: boolean;
  }[];
  /** The Parties list, with each row's party_role. */
  parties?: readonly {
    key: string;
    partyRole: string;
    displayName: string;
    email: string | null;
    contactId?: string | null;
  }[];
  /** The record's owner, when known. */
  owner?: { userId: string; displayName: string; email?: string | null } | null;
  /** Who holds a seat for the whole firm (firm_signing_roles). */
  firmRoles?: readonly {
    roleKey: string;
    userId: string;
    displayName: string;
    email: string | null;
  }[];
  /** transactions.other_party_name, the courtesy field the seller's
   *  (or buyer's) name is printed from on our copy. */
  otherPartyName?: string | null;
}

export type CastOutcome = "sure" | "ask" | "nobody" | "manual";

export interface CastSeat {
  role: SigningRole;
  outcome: CastOutcome;
  /** The person in the seat when the outcome is `sure`, or once someone
   *  has been picked. */
  person: CastPerson | null;
  /** The choices when the outcome is `ask`. */
  candidates: CastPerson[];
  /** Plain English for the row: where the person came from, or why the
   *  seat is empty. */
  reason: string;
  /** True once the advisor has said "leave them off this time". A
   *  deliberate click, never a default. */
  leftOff: boolean;
}

export type Casting = CastSeat[];

function orderedPeople(ctx: CastingContext): CastingContext["people"] {
  // Primary first, then the record's own order. A record whose people
  // arrive unordered must not put the co-borrower in seat one.
  const primary = ctx.people.filter((p) => p.isPrimary);
  const rest = ctx.people.filter((p) => !p.isPrimary);
  return [...primary, ...rest];
}

function asPerson(p: CastingContext["people"][number]): CastPerson {
  return { key: p.contactId, kind: "person", contactId: p.contactId, displayName: p.displayName, email: p.email };
}

function asParty(p: NonNullable<CastingContext["parties"]>[number]): CastPerson {
  return {
    key: p.key,
    kind: "party",
    contactId: p.contactId ?? null,
    displayName: p.displayName,
    email: p.email,
    partyRole: p.partyRole,
  };
}

/** Run every role's fill rule against the record. */
export function castRoles(roles: readonly SigningRole[], ctx: CastingContext): Casting {
  const people = orderedPeople(ctx);
  return roles.map((role): CastSeat => {
    const rule = role.fillRule;
    const why = fillRulePlainEnglish(rule);
    switch (rule.kind) {
      case "record_person": {
        const p = people[rule.index];
        if (p) return seat(role, "sure", asPerson(p), [], `From this record: ${why}`);
        return seat(
          role,
          "nobody",
          null,
          [],
          people.length === 0
            ? "Nobody on this record has been added yet"
            : `Only ${people.length === 1 ? "one person" : `${people.length} people`} on this record`,
        );
      }
      case "record_owner_user": {
        if (ctx.owner) {
          return seat(
            role,
            "sure",
            {
              key: "owner",
              kind: "owner",
              contactId: null,
              displayName: ctx.owner.displayName,
              email: ctx.owner.email ?? null,
              ownerUserId: ctx.owner.userId,
            },
            [],
            role.kind === "stamp"
              ? "Your saved signature is stamped before send. Nothing is emailed to you."
              : `From this record: ${why}`,
          );
        }
        return seat(role, "nobody", null, [], "This record has no owner yet");
      }
      case "party": {
        const hits = (ctx.parties ?? []).filter((p) => p.partyRole === rule.partyRole).map(asParty);
        const label = rule.partyRole.replace(/_/g, " ");
        if (hits.length === 1) return seat(role, "sure", hits[0]!, [], `From Parties: ${label}`);
        if (hits.length > 1) return seat(role, "ask", null, hits, `${hits.length} rows on Parties are tagged ${rule.partyRole.replace(/_/g, " ")}; pick one`);
        return seat(role, "nobody", null, [], `No ${rule.partyRole.replace(/_/g, " ")} on the Parties list yet`);
      }
      case "contract_card": {
        const name = ctx.otherPartyName?.trim();
        if (name) {
          return seat(
            role,
            "sure",
            { key: `name:${role.key}`, kind: "name", contactId: null, displayName: name, email: null },
            [],
            "From the Contract card. Printed, not asked to sign.",
          );
        }
        return seat(role, "nobody", null, [], "Nothing on the Contract card yet");
      }
      case "firm_role": {
        const holder = (ctx.firmRoles ?? []).find((f) => f.roleKey === rule.roleKey);
        if (!holder) return seat(role, "manual", null, [], "You pick each time");
        return seat(
          role,
          "sure",
          {
            key: `firm:${rule.roleKey}`,
            kind: "party",
            contactId: null,
            displayName: holder.displayName,
            email: holder.email,
            partyRole: rule.roleKey,
          },
          [],
          `Firm standard: ${why}`,
        );
      }
      case "manual":
        return seat(role, "manual", null, [], "You pick each time");
    }
  });
}

function seat(role: SigningRole, outcome: CastOutcome, person: CastPerson | null, candidates: CastPerson[], reason: string): CastSeat {
  return { role, outcome, person, candidates, reason, leftOff: false };
}

/** Put a person in a seat (the picker), or clear it. Returns a new casting. */
export function castSeat(casting: Casting, roleKey: string, person: CastPerson | null): Casting {
  return casting.map((s) =>
    s.role.key === roleKey
      ? { ...s, person, outcome: person ? "sure" : s.role.fillRule.kind === "manual" || s.role.fillRule.kind === "firm_role" ? "manual" : "nobody", leftOff: false, reason: person ? "Picked by you" : s.reason }
      : s,
  );
}

/** "Leave them off this time." A deliberate click on an empty seat. */
export function leaveSeatOff(casting: Casting, roleKey: string): Casting {
  return casting.map((s) => (s.role.key === roleKey ? { ...s, person: null, leftOff: true } : s));
}

/** Swap the record's first and second seats (Buyer 1 ↔ Buyer 2). */
export function swapFirstTwo(casting: Casting): Casting {
  const roles = casting.map((s) => s.role);
  const a = firstSeatOf(roles), b = secondSeatOf(roles);
  if (!a || !b) return casting;
  const sa = casting.find((s) => s.role.key === a.key);
  const sb = casting.find((s) => s.role.key === b.key);
  if (!sa || !sb) return casting;
  return casting.map((s) => {
    if (s.role.key === a.key) return { ...s, person: sb.person, outcome: sb.person ? "sure" : "nobody", leftOff: sb.leftOff, reason: sb.person ? "Swapped" : s.reason };
    if (s.role.key === b.key) return { ...s, person: sa.person, outcome: sa.person ? "sure" : "nobody", leftOff: sa.leftOff, reason: sa.person ? "Swapped" : s.reason };
    return s;
  });
}

/** Why the send is blocked, by role, or null when nothing blocks.
 *  Name-only seats never block (their lines print empty); a stamped
 *  seat with nobody blocks only if it has fields. */
export function castingBlockers(casting: Casting, fieldCountByRole: ReadonlyMap<string, number>): string[] {
  const out: string[] = [];
  for (const s of casting) {
    const fields = fieldCountByRole.get(s.role.key) ?? 0;
    if (s.role.kind === "name_only") continue;
    if (s.outcome === "ask") out.push(`${s.role.label}: pick one of ${s.candidates.length}`);
    else if (!s.person && !s.leftOff && fields > 0 && !s.role.optional && s.outcome !== "manual")
      out.push(`${s.role.label}: nobody in this seat, ${fields} ${fields === 1 ? "field" : "fields"} would be left off`);
    else if (!s.person && !s.leftOff && fields > 0 && s.role.kind === "signs" && s.outcome === "manual")
      out.push(`${s.role.label}: pick someone or leave them off`);
  }
  return out;
}

/** What the send needs per seat that has someone in it: the signer row
 *  the envelope records. Name-only seats are not signer rows (nobody is
 *  emailed and nothing is signed); their names are printed values on
 *  the placed fields and the casting is recorded on the envelope. */
export interface CastSigner {
  roleKey: string;
  roleKind: RoleKind;
  contactId: string | null;
  signerKey: string;
  name: string;
  email: string | null;
}

export function signersFromCasting(casting: Casting): CastSigner[] {
  const out: CastSigner[] = [];
  for (const s of casting) {
    if (!s.person || s.role.kind === "name_only") continue;
    out.push({
      roleKey: s.role.key,
      roleKind: s.role.kind,
      contactId: s.person.contactId,
      signerKey: s.person.kind === "person" && s.person.contactId ? s.person.contactId : `${s.role.key}:${s.person.key}`,
      name: s.person.displayName,
      email: s.person.email,
    });
  }
  return out;
}

/** The compact record of a casting written on the envelope: every seat,
 *  who filled it (or that it was left off), and the rule that proposed
 *  them. Names only; never an email or a contact's other details. */
export function castingRecord(casting: Casting): { roleKey: string; roleKind: RoleKind; name: string | null; contactId: string | null; outcome: CastOutcome; leftOff: boolean }[] {
  return casting.map((s) => ({
    roleKey: s.role.key,
    roleKind: s.role.kind,
    name: s.person?.displayName ?? null,
    contactId: s.person?.contactId ?? null,
    outcome: s.outcome,
    leftOff: s.leftOff,
  }));
}

/**
 * WHICH SEATS THE SENDER SIGNS THEMSELVES, RIGHT NOW, BEFORE IT GOES OUT.
 *
 * A `stamp` seat is one nobody is emailed about (Grant, 2026-08-22: we
 * never email ourselves an envelope). Its signature, initials and
 * date-signed lines are filled from the sender's own saved signature at
 * the moment they send, which is what "you sign your lines before it
 * goes out" means on the contract workflow mockup.
 *
 * Until this existed, those lines were simply never placed: a stamp seat
 * has no signer key by definition, and applyLayout refuses to place a
 * field whose seat is empty — then reported the seat as one "nobody is
 * in on this send". The sender IS in it. They are the one sending.
 *
 * THE RULE IS DELIBERATELY NARROW, and the narrowness is the point.
 * Three of the catalog's stamp seats belong to somebody who is NOT at
 * the keyboard: the designated broker's initials (34 of them in the
 * Realty atlas), a second agent's signature, and the record owner's when
 * an admin is sending on their behalf. Stamping the sender's signature
 * on any of those is not convenience, it is one person signing another
 * person's name. So a seat qualifies only when all four hold:
 *
 *   1. the catalog says the seat stamps rather than signs,
 *   2. somebody is actually in it,
 *   3. that somebody is the RECORD'S OWNER (the `owner` cast kind —
 *      never a contact, a party or a hand-typed name, none of which can
 *      be checked against a user id at all), and
 *   4. that owner IS the person sending.
 *
 * Rule 4 is why a `senderUserId` of null returns nothing rather than
 * everything: an unknown sender is not a matching sender.
 *
 * A sender with no saved signature qualifies for nothing either — there
 * is no image to stamp, and placing an empty box over their signature
 * line would look finished while being blank, which is the failure mode
 * this whole file exists to avoid.
 */
export function seatsTheSenderStamps(
  casting: Casting,
  senderUserId: string | null | undefined,
  hasSavedSignature: boolean,
): Set<string> {
  const out = new Set<string>();
  if (!senderUserId || !hasSavedSignature) return out;
  for (const seat of casting) {
    if (seat.role.kind !== "stamp") continue;
    if (seat.leftOff) continue;
    const person = seat.person;
    if (!person || person.kind !== "owner") continue;
    if (person.ownerUserId !== senderUserId) continue;
    out.add(seat.role.key);
  }
  return out;
}

/**
 * Fit a casting to the forms actually being sent (Grant, 2026-10-27: the
 * Commission Disbursement Authorization has one signature line, the
 * managing broker's, and the OS still cast the sellers to sign it).
 *
 * `formSeats` is every seat the package's forms have signer boxes for, or
 * null when any form's map can't say (seatsAPackageSigns). Null changes
 * nothing: the record is cast exactly as before.
 *
 * Known, a signing seat the forms never use is left off ("This form has no
 * line for them"), so it is neither ticked onto the send nor warned about as
 * empty. Every other seat is untouched; the advisor's own stamp is the
 * stamp's business, and anyone can still be added back by hand.
 */
export function fitCastingToForms(casting: Casting, formSeats: ReadonlySet<string> | null): Casting {
  if (!formSeats) return casting;
  return casting.map((s) =>
    s.role.kind === "signs" && !formSeats.has(s.role.key)
      ? { ...s, person: null, leftOff: true, reason: "The forms on this send have no line for this seat" }
      : s,
  );
}

/** Whether a firm seat (the managing broker) joins the send on its own:
 *  only when the forms have a line for it. Without that list it stays an
 *  offer on step 2, as before (most forms carry no broker line). */
export function firmSeatJoinsSend(roleKey: string, formSeats: ReadonlySet<string> | null): boolean {
  return formSeats !== null && formSeats.has(roleKey);
}

/**
 * The extra buyer (or seller) seat a send can take on, when its record has
 * fewer people than the deal has buyers.
 *
 * Nate, 2026-11-03: "Sometimes offers are really messy, and we have four
 * buyers purchasing a home. I need the ability to have three and four
 * buyers." Buyer 3 and Buyer 4 have been in the catalog since 2026-09-23,
 * but a send only included them when the RECORD had a third or fourth
 * person (seatAppliesToRecord). Buyers added by name and email on step 2
 * had no seat to go in.
 *
 * DECISION: offered one at a time, never all at once. Buyer 3 is offered
 * once every seat below it (Buyer 1, Buyer 2) has someone in it, and Buyer 4
 * once Buyer 3 does. A two-buyer send's Role dropdown therefore looks the
 * same as before, and nobody is offered "Buyer 4" while Buyer 2 sits empty.
 * The rejected alternative, always casting all four seats, put two empty
 * "Buyer 3 / Buyer 4" rows on every purchase send's field screen.
 */
export function extraSeatsOnOffer(roles: readonly SigningRole[], casting: Casting): SigningRole[] {
  const present = new Set(casting.map((s) => s.role.key));
  const next = roles
    .filter(
      (r) =>
        r.kind === "signs" &&
        r.fillRule.kind === "record_person" &&
        r.fillRule.index >= 2 &&
        !present.has(r.key),
    )
    .sort((a, b) => personIndex(a) - personIndex(b))[0];
  if (!next) return [];
  const below = casting.filter(
    (s) =>
      s.role.kind === "signs" &&
      s.role.fillRule.kind === "record_person" &&
      s.role.fillRule.index < personIndex(next),
  );
  return below.every((s) => s.person !== null) ? [next] : [];
}

function personIndex(role: SigningRole): number {
  return role.fillRule.kind === "record_person" ? role.fillRule.index : Number.MAX_SAFE_INTEGER;
}

/** Put an empty seat for `role` into the casting, in the catalog's order
 *  (Buyer 3 lands after Buyer 2, before the agents). A seat already there
 *  is left exactly as it is. */
export function addSeatToCasting(casting: Casting, role: SigningRole, roles: readonly SigningRole[]): Casting {
  if (casting.some((s) => s.role.key === role.key)) return casting;
  const seat: CastSeat = {
    role,
    outcome: "nobody",
    person: null,
    candidates: [],
    reason: "Added by you",
    leftOff: false,
  };
  const order = new Map(roles.map((r, i) => [r.key, i]));
  const at = order.get(role.key) ?? Number.MAX_SAFE_INTEGER;
  const index = casting.findIndex((s) => (order.get(s.role.key) ?? Number.MAX_SAFE_INTEGER) > at);
  return index === -1 ? [...casting, seat] : [...casting.slice(0, index), seat, ...casting.slice(index)];
}
